import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiCheckCircle,
  FiPlay,
} from "react-icons/fi";

import "./ProductDetail.css";

/* ─────────────────────────────────────
   IMAGE URL HELPERS
───────────────────────────────────────────── */
function prodImg(filename) {
  if (!filename) return null;
  const bare = String(filename).split(/[\\/]/).pop();
  return `${process.env.REACT_APP_API_URL}/uploads/products/${bare}`;
}

function thumbnailImg(filename) {
  if (!filename) return null;
  const bare = String(filename).split(/[\\/]/).pop();
  return `${process.env.REACT_APP_API_URL}/uploads/thumbnails/${bare}`;
}

/* ─────────────────────────────────────
   YOUTUBE EMBED HELPER
───────────────────────────────────────────── */
function getYouTubeEmbed(url) {
  if (!url) return null;
  if (url.includes("youtube.com/embed/")) return url;
  let m = url.match(/[?&]v=([^&#]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/youtu\.be\/([^?&#]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  if (/^[\w-]{11}$/.test(url.trim())) return `https://www.youtube.com/embed/${url.trim()}`;
  return null;
}

/* ─────────────────────────────────────
   STRIP HTML TAGS — returns plain text
───────────────────────────────────────────── */
function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ─────────────────────────────────────
   PARSE VIDEO DESCRIPTION HTML
───────────────────────────────────────────── */
function parseVideoDescription(html) {
  if (!html) return { prose: null, ulItems: [], olItems: [] };

  const withoutLists = html.replace(/<(ul|ol)[\s\S]*?<\/\1>/gi, "");
  const prose = stripHtml(withoutLists).trim() || null;

  const extractItems = (block) => {
    const items = [];
    const re = /<li[^>]*>([\s\S]*?)<\/li>/gi;
    let m;
    while ((m = re.exec(block)) !== null) {
      const text = stripHtml(m[1]).trim();
      if (text) items.push(text);
    }
    return items;
  };

  let ulItems = [];
  let olItems = [];
  const listRe = /<(ul|ol)[^>]*>([\s\S]*?)<\/\1>/gi;
  let match;
  while ((match = listRe.exec(html)) !== null) {
    const tag = match[1].toLowerCase();
    const items = extractItems(match[2]);
    if (tag === "ul") ulItems = ulItems.concat(items);
    else olItems = olItems.concat(items);
  }

  return { prose, ulItems, olItems };
}

/* ─────────────────────────────────────
   NO RIGHT-CLICK
───────────────────────────────────────────── */
function useNoContextMenu() {
  useEffect(() => {
    const h = (e) => e.preventDefault();
    document.addEventListener("contextmenu", h);
    return () => document.removeEventListener("contextmenu", h);
  }, []);
}

const noSaveProps = {
  draggable: false,
  onDragStart:   (e) => e.preventDefault(),
  onContextMenu: (e) => e.preventDefault(),
};

/* ─────────────────────────────────────
   LIGHTBOX
───────────────────────────────────────────── */
function Lightbox({ images, activeIdx, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowLeft")  onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (activeIdx === null || !images[activeIdx]) return null;

  return (
    <div className="pd-lightbox" onClick={onClose}>
      <button className="pd-lightbox__close" onClick={onClose}>
        <FiX size={22} />
      </button>
      <button
        className="pd-lightbox__nav pd-lightbox__nav--prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        <FiChevronLeft size={28} />
      </button>
      <div className="pd-lightbox__stage" onClick={(e) => e.stopPropagation()}>
        <img src={images[activeIdx].src} alt={images[activeIdx].caption} {...noSaveProps} />
        <div className="pd-lightbox__caption">
          <span className="pd-lightbox__count">{activeIdx + 1} / {images.length}</span>
          {images[activeIdx].caption}
        </div>
      </div>
      <button
        className="pd-lightbox__nav pd-lightbox__nav--next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        <FiChevronRight size={28} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────
   SKELETON
───────────────────────────────────────────── */
function ProductDetailSkeleton() {
  return (
    <main className="pd">
      <section style={{ minHeight: 460, background: "#1e1e1f", position: "relative" }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(90deg,#2a2a2b 25%,#333 50%,#2a2a2b 75%)",
          backgroundSize: "200% 100%",
          animation: "pdShimmer 1.4s infinite",
        }} />
      </section>
      <section style={{ padding: "80px 0" }}>
        <div className="container pd-body__inner">
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ height: 360, background: "#f0f0f0", borderRadius: 2 }} />
            <div style={{ height: 280, background: "#f0f0f0", borderRadius: 2 }} />
          </div>
          <div style={{ width: 300, background: "#f0f0f0", borderRadius: 2, height: 400, flexShrink: 0 }} />
        </div>
      </section>
    </main>
  );
}

/* ─────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function ProductDetail() {
  useNoContextMenu();

  const { slug } = useParams();

  const [product,     setProduct]     = useState(null);
  const [video,       setVideo]       = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [activeThumb, setActiveThumb] = useState(0);

  /* ── All products for sidebar & prev/next ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/products`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setAllProducts(Array.isArray(data) ? data.filter((p) => p.active) : []))
      .catch(() => setAllProducts([]));
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    setProduct(null);
    setVideo(null);
    setActiveThumb(0);
    window.scrollTo(0, 0);

    fetch(`${process.env.REACT_APP_API_URL}/api/products/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((data) => {
        if (!data || !data.id) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setProduct(data);

        const tryVideo = (url) =>
          fetch(url)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);

        tryVideo(`${process.env.REACT_APP_API_URL}/api/videos/by-title/${encodeURIComponent(slug)}`)
          .then((vid) => {
            if (vid && vid.id) return vid;
            return tryVideo(`${process.env.REACT_APP_API_URL}/api/videos/by-title/${encodeURIComponent(data.title)}`);
          })
          .then((vid) => { if (vid && vid.id) setVideo(vid); })
          .finally(() => setLoading(false));
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  const images = product && product.image_filename
    ? [{ src: prodImg(product.image_filename), caption: product.title || "" }]
    : [];

  const openLightbox = useCallback((i) => {
    setLightboxIdx(i);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
    document.body.style.overflow = "";
  }, []);

  const prevLight = useCallback(() => {
    setLightboxIdx((i) => (i <= 0 ? images.length - 1 : i - 1));
  }, [images.length]);

  const nextLight = useCallback(() => {
    setLightboxIdx((i) => (i >= images.length - 1 ? 0 : i + 1));
  }, [images.length]);

  if (loading) return <ProductDetailSkeleton />;

  if (notFound || !product) {
    return (
      <div className="pd-not-found">
        <h2>Product not found</h2>
        <Link to="/products" className="pd-back-btn">
          <FiArrowLeft size={16} /> Back to Products
        </Link>
      </div>
    );
  }

  const hasImages    = images.length > 0;
  const heroBg       = hasImages ? images[0].src : null;
  const embedUrl     = video ? getYouTubeEmbed(video.video_url) : null;
  const thumbnailSrc = video && video.thumbnail_filename ? thumbnailImg(video.thumbnail_filename) : null;

  const { prose, ulItems, olItems } = parseVideoDescription(video?.description || "");

  const otherProducts = allProducts.filter((p) => p.slug !== slug);
  const currentIdx    = allProducts.findIndex((p) => p.slug === slug);
  const prevProd      = currentIdx > 0                      ? allProducts[currentIdx - 1] : null;
  const nextProd      = currentIdx < allProducts.length - 1 ? allProducts[currentIdx + 1] : null;
  const prodPath      = (p) => `/products/${p.slug}`;

  return (
    <main className="pd">

      {/* ══════ 1. HERO ══════ */}
      <section className="pd-hero">
        <div
          className="pd-hero__bg"
          style={heroBg ? { backgroundImage: `url(${heroBg})` } : { background: "#1e1e1f" }}
        />
        <div className="pd-hero__overlay" />
        <div className="pd-hero__stripe" aria-hidden="true" />

        <div className="container pd-hero__body">
          <Link to="/products" className="pd-ani pd-ani--1 pd-hero__back">
            <FiArrowLeft size={14} /> All Products
          </Link>
          <div className="pd-ani pd-ani--2 pd-hero__label">PRODUCT DETAIL</div>
          <h1 className="pd-ani pd-ani--3 pd-hero__title">{product.title}</h1>
        </div>

        <div className="pd-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={11} />
          <Link to="/products">Products</Link>
          <FiArrowRight size={11} />
          <span>{product.title}</span>
        </div>
      </section>

      {/* ══════ 2. BODY ══════ */}
      <section className="pd-body">
        <div className="container pd-body__inner">

          <div className="pd-main">

            {olItems.length > 0 && (
              <div className="pd-ani pd-ani--1 pd-desc__icon-grid">
                {olItems.map((item, i) => (
                  <div key={i} className="pd-desc__icon-card">
                    <div className="pd-desc__icon-wrap"><FiCheckCircle size={16} /></div>
                    <span className="pd-desc__icon-label">{item}</span>
                  </div>
                ))}
              </div>
            )}

            {(hasImages || embedUrl || thumbnailSrc) && (
              <div className="pd-ani pd-ani--1 pd-media">

                {hasImages && (
                  <div className="pd-media__image-col">
                    <div className="pd-media__photo" onClick={() => openLightbox(activeThumb)}>
                      <img
                        src={images[activeThumb].src}
                        alt={images[activeThumb].caption}
                        {...noSaveProps}
                      />
                      <div className="pd-media__photo-overlay">
                        <span className="pd-media__photo-zoom">⤡ Click to enlarge</span>
                      </div>
                      <div className="pd-media__photo-badge">
                        {activeThumb + 1}/{images.length}
                      </div>
                    </div>

                    {images.length > 1 && (
                      <div className="pd-media__thumbs">
                        {images.map((img, i) => (
                          <button
                            key={i}
                            className={`pd-media__thumb ${i === activeThumb ? "pd-media__thumb--active" : ""}`}
                            onClick={() => setActiveThumb(i)}
                          >
                            <img src={img.src} alt={img.caption} {...noSaveProps} />
                            {i === activeThumb && <div className="pd-media__thumb-bar" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(embedUrl || thumbnailSrc) && (
                  <div className="pd-media__video-col">
                    {embedUrl ? (
                      <div className="pd-media__video-wrap">
                        <iframe
                          src={embedUrl}
                          title={video?.title || product.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : thumbnailSrc ? (
                      <div className="pd-media__video-wrap pd-media__video-thumb">
                        <img
                          src={thumbnailSrc}
                          alt={video?.title || product.title}
                          className="pd-media__thumb-img"
                          {...noSaveProps}
                        />
                        <div className="pd-media__thumb-overlay">
                          <div className="pd-media__thumb-play">
                            <FiPlay size={28} />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {embedUrl && thumbnailSrc && (
                      <div className="pd-media__video-thumbnail-strip">
                        <img
                          src={thumbnailSrc}
                          alt={`${video?.title || product.title} thumbnail`}
                          className="pd-media__video-tn-img"
                          {...noSaveProps}
                        />
                      </div>
                    )}

                    {video?.title && (
                      <div className="pd-media__video-label">
                        <FiPlay size={14} />
                        {video.title}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {(prose || ulItems.length > 0) && (
              <div className="pd-ani pd-ani--2 pd-desc">
                {prose && (
                  <div className="pd-desc__prose">
                    {prose.split(/\n\n+/).map((para, i) => {
                      // Split paragraph into sentences by full stop (keep the period)
                      const sentences = para
                        .split(/(?<=\.)\s+/)
                        .map(s => s.trim())
                        .filter(Boolean);

                      return (
                        <p key={i} style={{ marginBottom: 12 }}>
                          {sentences.map((sentence, j) => {
                            // Match "Label: rest of sentence" pattern
                            const labelMatch = sentence.match(/^([^:]{1,60}):\s*(.*)$/);
                            if (labelMatch) {
                              return (
                                <span key={j} style={{ display: "block" }}>
                                  <strong>{labelMatch[1]}:</strong>{" "}
                                  {labelMatch[2]}
                                </span>
                              );
                            }
                            return (
                              <span key={j} style={{ display: "block" }}>
                                {sentence}
                              </span>
                            );
                          })}
                        </p>
                      );
                    })}
                  </div>
                )}
                {ulItems.length > 0 && (
                  <div className="pd-desc__list">
                    {ulItems.map((item, i) => (
                      <div key={i} className="pd-desc__item">
                        <div className="pd-desc__item-dot" />
                        <p className="pd-desc__item-text">{item}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!prose && !ulItems.length && !video && product.description && (
              <div className="pd-ani pd-ani--2 pd-desc">
                <div className="pd-desc__prose">
                  <div dangerouslySetInnerHTML={{ __html: product.description }} />
                </div>
              </div>
            )}

          </div>

          <aside className="pd-ani pd-ani--2 pd-sidebar">
            {otherProducts.length > 0 && (
              <div className="pd-sidebar__block">
                <div className="pd-sidebar__head">OTHER PRODUCTS</div>
                <ul className="pd-sidebar__list">
                  {otherProducts.map((p) => (
                    <li key={p.id}>
                      <Link to={prodPath(p)} className="pd-sidebar__link">
                        {p.title}
                        <FiArrowRight size={13} className="pd-sidebar__arrow" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Link to="/shop" className="pd-sidebar__cta">
              SHOP HERE <FiArrowRight size={14} />
            </Link>
          </aside>
        </div>
      </section>

      {/* ══════ 3. PREV / NEXT ══════ */}
      <nav className="pd-service-nav">
        <div className="container pd-service-nav__inner">
          {prevProd ? (
            <Link to={prodPath(prevProd)} className="pd-service-nav__btn pd-service-nav__btn--prev">
              <FiChevronLeft size={20} />
              <span><small>Previous Product</small>{prevProd.title}</span>
            </Link>
          ) : <div />}

          <Link to="/products" className="pd-service-nav__all">All Products</Link>

          {nextProd ? (
            <Link to={prodPath(nextProd)} className="pd-service-nav__btn pd-service-nav__btn--next">
              <span><small>Next Product</small>{nextProd.title}</span>
              <FiChevronRight size={20} />
            </Link>
          ) : <div />}
        </div>
      </nav>

      {/* ══════ 4. CTA BAND ══════ */}
      <section className="pd-cta">
        <div className="container pd-cta__inner">
          <div className="pd-cta__text">
            <h3>Interested in this product?</h3>
            <p>Contact our team for pricing, availability and fitment advice.</p>
          </div>
          <Link to="/contact" className="pd-cta__btn">
            Get a Quote <FiArrowRight size={16} />
          </Link>
        </div>
      </section>

      {hasImages && (
        <Lightbox
          images={images}
          activeIdx={lightboxIdx}
          onClose={closeLightbox}
          onPrev={prevLight}
          onNext={nextLight}
        />
      )}
    </main>
  );
}